import {omit} from 'lodash-es';
import {v4 as uuidV4} from 'uuid';
import {createAction, createAsyncThunk} from '@reduxjs/toolkit';
import {
    getFlexRates,
    getFlexRatesMatrices,
    previewFlexRatesMatrix,
    saveFlexRates,
} from 'api/rates/flex-rates/flex-rates';
import {getEnabledRatingRuleGroups} from 'api/rates/rating-rule-groups';
import {asDecimalInteger, formatPrice} from 'utils/number-format';
import {
    formatValidationErrors,
    multiDayDiscountsSchema,
    rateBandsSchema,
    rateSegmentsSchema,
} from 'pages/rates/flex-rates/utils/validationSchemas';
import {
    rateBandsEntityAdapter,
    rateSegmentsEntityAdapter,
} from './flex-rates-adapters';
import {
    generateRateSegmentDescription,
    CATEGORIZED_ALGORITHMS,
} from '../utils/utils';
import getWeeklyHours from 'api/facilities/get-weekly-hours';
import getMultiDayDiscounts from 'api/rates/multi-day-discounts/get-multi-day-discounts';
import updateMultiDayDiscounts from 'api/rates/multi-day-discounts/update-multi-day-discounts';
import getMultiDayDiscountsExample from 'api/rates/multi-day-discounts/get-multi-day-discounts-example';

/**
 * Thunk that gets the default inventory for a facility.
 */
export const getHoursOfOperation = createAsyncThunk(
    'inventory/getHoursOfOperation',
    /**
     * @param {number} facilityId
     * @param rejectWithValue
     */
    async (facilityId, {rejectWithValue}) => {
        try {
            const response = await getWeeklyHours(facilityId);

            return response;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

/**
 * Action to update the interpolation value in the store
 * @param {TFlexRateConfig.interpolation.algorithm} interpolation - The interpolation value to update
 */
export const updateInterpolation = createAction(
    'flexRates/updateInterpolation'
);

/**
 * Action to delete a rateBand by duration
 * @param {number} duration - The duration of the rate band to delete
 */
export const deleteRateBand = createAction('flexRates/deleteRateBand');

/**
 * Action to delete a multi-day discount by duration
 * @param {number} duration - The duration of the rate band to delete
 */
export const deleteMultiDayDiscount = createAction(
    'flexRates/deleteMultiDayDiscount'
);

/**
 * Action to delete a rateSegment by customRateId
 * @param {string} customRateId - The id of the rate band to delete
 */
export const deleteRateSegment = createAction('flexRates/deleteRateSegment');

/**
 * @param {boolean} isPreview - Whether the user is in preview mode
 */
export const setPreviewState = createAction(
    'flexRates/setPreviewState',
    (isPreview) => ({payload: isPreview})
);

/**
 * Thunk that retrieves the flex rates rule groups for a given facility ID.
 */
export const getFlexRatesRatingRuleGroups = createAsyncThunk(
    'flexRates/getFlexRatesRatingRuleGroups',
    /**
     * @param {object} params - The parameters object
     * @param {number} params.facilityId - The ID of the facility.
     * @param {boolean} params.isAirportPage - Whether to show on the Airport page
     * @param {object} thunkAPI - The thunk API object
     * @param {function} thunkAPI.rejectWithValue - A function to handle the error and return a rejected promise.
     * @returns {Promise<Array>} - A promise that resolves to an array of enabled rating rule groups.
     * @throws {Error} - if no flex rates rule group is found.
     */
    async ({facilityId, isAirportPage}, {rejectWithValue}) => {
        try {
            const response = await getEnabledRatingRuleGroups(
                facilityId,
                isAirportPage
            );

            const ratingRuleGroups = response.filter((ruleGroup) =>
                CATEGORIZED_ALGORITHMS.flexRates.includes(
                    ruleGroup.algorithmType
                )
            );

            if (!ratingRuleGroups.length) {
                return rejectWithValue(`No flex rates rule group found.`);
            }

            return ratingRuleGroups;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

/**
 * Thunk that gets the flex rates and adds them to the redux store
 */
export const getFlexRatesData = createAsyncThunk(
    'flexRates/getFlexRatesData',
    /**
     * @param {object} args - Arguments object.
     * @param {number} args.facilityId - The ID of the facility.
     * @param {number} [args.ruleGroupId] - The ID of the rule group (optional).
     * @param {boolean} args.isAirportPage - Whether to show on the Airport page.
     * @param {object} thunkAPI - The thunk API object
     * @param {function} thunkAPI.dispatch - A dispatch function used to call the getFlexRatesRatingRuleGroups action.
     * @param {function} thunkAPI.rejectWithValue - A rejectWithValue function used to handle error values.
     * @return {Promise} A Promise that resolves to the flex rates data or rejects with an error value.
     */
    async (
        {facilityId, ruleGroupId, isAirportPage},
        {dispatch, rejectWithValue}
    ) => {
        try {
            const flexRatesRuleGroups = await dispatch(
                getFlexRatesRatingRuleGroups({facilityId, isAirportPage})
            ).unwrap();

            const flexRatesRuleGroupId =
                ruleGroupId || flexRatesRuleGroups[0].id;

            const flexRatesData = await getFlexRates(
                flexRatesRuleGroupId,
                isAirportPage
            );

            flexRatesData.config.ruleGroupId = flexRatesRuleGroupId;

            return flexRatesData;
        } catch (err) {
            console.log(err);

            return rejectWithValue(err);
        }
    }
);

/**
 * Saves flex rates data to the server.
 */
export const saveFlexRatesData = createAsyncThunk(
    'flexRates/saveFlexRates',
    /**
     * @param data
     * @param {TFlexRateFormattedRateBand[]} data.rateBands
     * @param {TFlexRateFormattedRateSegment[]} data.rateSegments
     * @param {TFlexRateConfig.interpolation} data.interpolation
     * @param {number} data.ruleGroupId
     * @param rejectWithValue
     * @param dispatch
     */
    async (
        {rateBands, rateSegments, interpolation, ruleGroupId},
        {rejectWithValue}
    ) => {
        try {
            const data = {rateBands, rateSegments, interpolation};
            // make the api call to the update endpoint
            const response = await saveFlexRates(data, ruleGroupId);

            response.config.ruleGroupId = ruleGroupId;

            return response;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

/**
 * Thunk that updates and validates the rate bands in the store with a single rate band
 */
export const updateAndValidateRateBands = createAsyncThunk(
    'flexRates/updateAndValidateRateBands',
    async ({duration, rate, day}, {getState, rejectWithValue}) => {
        try {
            // build a collection of rate bands with the updated rate band value
            const nextState = rateBandsEntityAdapter.updateOne(
                getState().flexRates.rateBands,
                {
                    id: duration,
                    changes: {
                        [day]: rate
                            ? formatPrice(rate * 100, {
                                  formatterCallback: asDecimalInteger,
                              })
                            : null,
                    },
                }
            );

            // select the normalized rate bands from the previously built collection
            const rateBands = rateBandsEntityAdapter
                .getSelectors()
                .selectAll(nextState);

            // using a yup schema, validate the rate bands
            await rateBandsSchema.validate(rateBands);

            // update the redux store with the rate bands
            return rateBands;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const addAndValidateNewRateBand = createAsyncThunk(
    'flexRates/addAndValidateNewRateBand',
    async ({duration, rate, daysOfWeek}, {getState, rejectWithValue}) => {
        try {
            // build a collection of rate bands with the updated rate band value
            const nextState = rateBandsEntityAdapter.upsertOne(
                getState().flexRates.rateBands,
                {
                    duration,
                    daysOfWeek: daysOfWeek ?? [],
                    rate: rate ?? 0,
                    ...daysOfWeek?.reduce((acc, day) => {
                        return {
                            ...acc,
                            [day.toLowerCase()]: rate
                                ? formatPrice(rate * 100, {
                                      formatterCallback: asDecimalInteger,
                                  })
                                : null,
                        };
                    }, {}),
                }
            );

            // select the normalized rate bands from the previously built collection
            const rateBands = rateBandsEntityAdapter
                .getSelectors()
                .selectAll(nextState);

            // using a yup schema, validate the rate bands
            await rateBandsSchema.validate(rateBands, {abortEarly: false});

            // remove the unnecessary rate band properties before updating the store to avoid throwing errors when posting to the API endpoint - `daysOfWeek` and `rate` are only used to validate the form.
            const formattedRateBands = rateBands.map((rateBand) =>
                omit(rateBand, ['daysOfWeek', 'rate'])
            );

            // update the redux store with the rate bands
            return formattedRateBands;
        } catch (error) {
            const formattedError = formatValidationErrors(error);

            return rejectWithValue(formattedError);
        }
    }
);

/**
 * Formats the rate segment to be saved to the server. Handles the conditional presence of the `triggerType` and `triggerTime` properties, the creation of the `description` property, and the formatting of the `minRate` and `maxRate` properties.
 * @function formatRateSegments
 * @param {TFlexRateFormattedRateSegment} modifiedRateSegment - The modified rate segment to format
 * @param {TFlexRateFormattedRateSegment} previousRateSegment - The previous version of the rate segment being modified
 * @param {TFlexRateConfig.interpolation} interpolation - The interpolation value
 * @returns {TFlexRateFormattedRateSegment} The formatted rate segment object (conditionally includes the `triggerType` and `triggerTime` properties)
 */
const formatRateSegments = ({
    modifiedRateSegment,
    previousRateSegment,
    interpolation,
}) => {
    if (
        !modifiedRateSegment.triggerType &&
        previousRateSegment.triggerType === 'ExitAfter'
    ) {
        modifiedRateSegment.triggerType = previousRateSegment.triggerType;
        modifiedRateSegment.triggerTime = previousRateSegment.triggerTime;
    } else if (!modifiedRateSegment.triggerType) {
        delete modifiedRateSegment.triggerTime;
        delete modifiedRateSegment.triggerType;
    }

    return {
        ...previousRateSegment,
        customName: modifiedRateSegment.customName,
        customRateId:
            modifiedRateSegment.customRateId || uuidV4().replaceAll('-', ''),
        daysOfWeek: modifiedRateSegment.daysOfWeek,
        description: generateRateSegmentDescription({
            earliestEntryTime: modifiedRateSegment.earliestEntryTime,
            latestEntryTime: modifiedRateSegment.latestEntryTime,
            minDuration: modifiedRateSegment.minDuration,
            maxDuration: modifiedRateSegment.maxDuration,
            ...(modifiedRateSegment.triggerType && {
                triggerType: modifiedRateSegment.triggerType,
            }),
            ...(modifiedRateSegment.triggerTime && {
                triggerTime: modifiedRateSegment.triggerTime,
            }),
        }),
        earliestEntryTime: modifiedRateSegment.earliestEntryTime,
        latestEntryTime: modifiedRateSegment.latestEntryTime,
        maxDuration: modifiedRateSegment.maxDuration,
        maxRate: modifiedRateSegment.maxRate
            ? asDecimalInteger(modifiedRateSegment.maxRate)
            : null,
        minDuration: modifiedRateSegment.minDuration,
        minRate: modifiedRateSegment.minRate
            ? asDecimalInteger(modifiedRateSegment.minRate)
            : // if the interpolation is set to none, default the minRate to the same value as the maxRate
              asDecimalInteger(modifiedRateSegment.maxRate),
        ...(modifiedRateSegment.triggerType && {
            triggerType: modifiedRateSegment.triggerType,
        }),
        ...(modifiedRateSegment.triggerTime && {
            triggerTime: modifiedRateSegment.triggerTime,
        }),
        interpolation,
    };
};

/**
 * Thunk that updates and validates the Segment rate bands in the store with a single rate band
 */
export const updateAndValidateRateSegments = createAsyncThunk(
    'flexRates/updateAndValidateRateSegments',
    async ({modifiedRateSegment}, {getState, rejectWithValue}) => {
        try {
            const state = getState().flexRates.rateSegments;
            const interpolation = getState().flexRates.interpolation;

            const previousRateSegment = rateSegmentsEntityAdapter
                .getSelectors()
                .selectById(state, modifiedRateSegment.customRateId);

            // build a collection of rate bands with the updated rate band value
            const nextState = rateSegmentsEntityAdapter.setOne(state, {
                ...formatRateSegments({
                    modifiedRateSegment,
                    previousRateSegment: previousRateSegment || {},
                    interpolation,
                }),
            });

            // select the normalized rate bands from the previously built collection
            const rateSegments = rateSegmentsEntityAdapter
                .getSelectors()
                .selectAll(nextState);

            // // using a yup schema, validate the rate bands
            await rateSegmentsSchema.validate(rateSegments, {
                abortEarly: false,
            });

            // update the redux store with the rate bands
            return rateSegments;
        } catch (error) {
            const formattedError = formatValidationErrors(error);

            return rejectWithValue(formattedError);
        }
    }
);

/**
 * Thunk that gets the flex rates matrices and adds them to the redux store
 */
export const getFlexRatesMatrixData = createAsyncThunk(
    'flexRates/getFlexRatesMatrixData',
    /**
     * @param {number} flexRateId
     * @param rejectWithValue
     */
    async (flexRateId, {rejectWithValue}) => {
        try {
            const response = await getFlexRatesMatrices(flexRateId);

            return response;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

/**
 * Thunk that gets the flex rates matrices from the preview endpoint and adds them to the redux store
 */
export const previewFlexRatesMatrixData = createAsyncThunk(
    'flexRates/previewFlexRatesMatrixData',
    /**
     * @param data
     * @param {TFlexRateFormattedRateBand[]} data.rateBands
     * @param {TFlexRateFormattedRateSegment[]} data.rateSegments
     * @param {TFlexRateConfig.interpolation} data.interpolation
     * @param rejectWithValue
     */
    async ({rateBands, rateSegments, interpolation}, {rejectWithValue}) => {
        try {
            const response = await previewFlexRatesMatrix({
                rateBands,
                rateSegments,
                interpolation,
            });

            return response;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

/**
 * Thunk that retrieves multi-day discount data for a facility and rule group.
 */
export const getMultiDayDiscountData = createAsyncThunk(
    'flexRates/getMultiDayDiscounts',
    /**
     * @param {object} args - Arguments object.
     * @param {number} args.facilityId - The ID of the facility.
     * @param {number} [args.ruleGroupId] - The ID of the rule group (optional).
     * @param {boolean} args.isAirportPage - Whether to show on the Airport page.
     * @param {function} dispatch - A dispatch function used to call the getFlexRatesRatingRuleGroups action.
     * @param {function} rejectWithValue - A function to handle the error and return a rejected promise.
     * @returns {Promise<Array>} - A promise that resolves to an array of multi-day discounts.
     * @throws {Error} - if no multi-day discounts are found.
     */
    async (
        {facilityId, ruleGroupId, isAirportPage},
        {dispatch, rejectWithValue}
    ) => {
        try {
            const flexRatesRuleGroups = await dispatch(
                getFlexRatesRatingRuleGroups({facilityId, isAirportPage})
            ).unwrap();

            const flexRatesRuleGroupId =
                ruleGroupId || flexRatesRuleGroups[0].id;

            const response = await getMultiDayDiscounts({
                facilityId: Number(facilityId),
                ruleGroupId: Number(flexRatesRuleGroupId),
            });

            return response;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

/**
 * Thunk that updates and validates multi-day discounts.
 */
export const updateAndValidateMultiDayDiscounts = createAsyncThunk(
    'flexRates/updateAndValidateMultiDayDiscounts',
    /**
     * @param {number} amount - The discount amount.
     * @param {string} duration - The duration of the multi-day discount.
     * @param {number} facilityId - The ID of the facility.
     * @param {number} ruleGroupId - The ID of the rule group.
     * @param {function} rejectWithValue - A function to handle the error and return a rejected promise.
     * @returns {Promise<Array>} - A promise that resolves to an array of updated multi-day discounts.t
     * @throws {Error}
     */
    async ({amount, duration, facilityId, ruleGroupId}, {rejectWithValue}) => {
        try {
            await multiDayDiscountsSchema.validate({
                amount,
                duration,
            });

            const updatedDiscount = await getMultiDayDiscountsExample({
                facilityId,
                ruleGroupId,
                options: {amount, duration},
            });

            return updatedDiscount;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

/**
 * Thunk that adds a new multi-day discount and validates it.
 */
export const addAndValidateNewMultiDayDiscount = createAsyncThunk(
    'flexRates/addAndValidateNewMultiDayDiscount',
    /**
     * @param {number} amount - The discount amount.
     * @param {string} duration - The duration of the multi-day discount.
     * @param {object} example - An example reservation object.
     * @param {function} getState - A function to get the current state of the store.
     * @param {function} rejectWithValue - A function to handle the error and return a rejected promise.
     * @returns {object} - A promise that resolves to an array of updated multi-day discounts.
     * @throws {Error}
     */
    async ({amount, duration, example}, {rejectWithValue}) => {
        try {
            await multiDayDiscountsSchema.validate(
                {amount, duration, example},
                {
                    abortEarly: false,
                }
            );

            return {amount, duration, example};
        } catch (error) {
            const formattedError = formatValidationErrors(error);

            return rejectWithValue(formattedError);
        }
    }
);

/**
 * Thunk that saves multi-day discounts to the back end.
 */
export const saveMultiDayDiscounts = createAsyncThunk(
    'flexRates/saveMultiDayDiscounts',
    /**
     * @param {number} facilityId - The ID of the facility.
     * @param {number} ruleGroupId - The ID of the rule group.
     * @param {Array} multiDayDiscounts - The multi-day discounts to save.
     * @param {function} rejectWithValue - A function to handle the error and return a rejected promise.
     * @returns {Promise<Array>} - A promise that resolves to the saved multi-day discounts.
     * @throws {Error} - if the save operation fails.
     */
    async ({facilityId, ruleGroupId, multiDayDiscounts}, {rejectWithValue}) => {
        try {
            const response = await updateMultiDayDiscounts({
                discounts: multiDayDiscounts,
                facilityId: Number(facilityId),
                ruleGroupId: Number(ruleGroupId),
            });

            return response;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

/**
 * Action to reset the rate matrices
 */
export const resetRateMatrices = createAction('flexRates/resetRateMatrices');
